/**
 * AC-M7-6 — Inspector Tab
 * Governance surface for a session: skills loaded, memories retrieved, budget, connections.
 * Admin/owner only.
 */
import React from 'react';
import { t } from '@/lib/i18n';
import { apiFetch } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { formatTime, formatNumber } from '@/lib/localeFormatters';
import { LoadingState } from '@/components/ui/LoadingState';

interface InspectorData {
  skills: Array<{ name: string; loadedAt: string }>;
  memoriesRetrieved: Array<{ key: string; category: string; content: string }>;
  budget: {
    inputTokens: number;
    outputTokens: number;
    estimatedCostUsd: number;
    modelName: string;
  };
  connectionsUsed: Array<{ connectionName: string; toolName: string; calledAt: string }>;
}

function SectionHeader({ title }: { title: string }) {
  return (
    <h3 className="text-sm font-semibold text-slate-700 mb-2">{title}</h3>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <p className="text-xs text-slate-400 italic py-1">{message}</p>
  );
}

interface InspectorTabProps {
  sessionId: string;
  isAdmin: boolean;
}

export function InspectorTab({ sessionId, isAdmin }: InspectorTabProps) {
  const { data, isLoading, error } = useQuery<InspectorData>({
    queryKey: ['session-inspector', sessionId],
    queryFn: async () => {
      const res = await apiFetch(`/api/v1/agent-sessions/${sessionId}/inspector`);
      if (!res.ok) throw new Error('Failed to load inspector data');
      const json = await res.json();
      return json.data as InspectorData;
    },
    enabled: isAdmin,
    staleTime: 30_000,
  });

  if (!isAdmin) {
    return (
      <div className="p-4 text-sm text-slate-500" data-testid="inspector-admin-only">
        {t('Inspector is available to admins and owners only.')}
      </div>
    );
  }

  if (isLoading) {
    return <LoadingState size="sm" minHeight="80px" />;
  }

  if (error || !data) {
    return (
      <div className="p-4 text-sm text-red-500" data-testid="inspector-error">
        {t('Failed to load inspector data.')}
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 text-sm" data-testid="inspector-tab">
      {/* Skills */}
      <section>
        <SectionHeader title={t('Skills Loaded')} />
        {data.skills.length === 0 ? (
          <EmptyState message={t('No skills loaded in this session.')} />
        ) : (
          <div className="space-y-1">
            {data.skills.map((s) => (
              <div key={s.name} className="flex items-center justify-between py-1 border-b border-slate-100">
                <span className="font-mono text-xs text-slate-700">{s.name}</span>
                <span className="text-xs text-slate-400">{formatTime(s.loadedAt)}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Memories Retrieved */}
      <section>
        <SectionHeader title={t('Memories Retrieved')} />
        {data.memoriesRetrieved.length === 0 ? (
          <EmptyState message={t('No memories retrieved in this session.')} />
        ) : (
          <div className="space-y-1">
            {data.memoriesRetrieved.map((m, i) => (
              <div key={i} className="py-1 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <span className="text-xs bg-slate-100 text-slate-600 px-1 rounded">{m.category}</span>
                  <span className="font-mono text-xs text-slate-700">{m.key}</span>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">{m.content}</p>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Budget */}
      <section>
        <SectionHeader title={t('Token Budget')} />
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="bg-slate-50 rounded p-2">
            <p className="text-slate-500">{t('Model')}</p>
            <p className="font-medium text-slate-700">{data.budget.modelName}</p>
          </div>
          <div className="bg-slate-50 rounded p-2">
            <p className="text-slate-500">{t('Input tokens')}</p>
            <p className="font-medium text-slate-700">{formatNumber(data.budget.inputTokens)}</p>
          </div>
          <div className="bg-slate-50 rounded p-2">
            <p className="text-slate-500">{t('Output tokens')}</p>
            <p className="font-medium text-slate-700">{formatNumber(data.budget.outputTokens)}</p>
          </div>
          <div className="bg-slate-50 rounded p-2">
            <p className="text-slate-500">{t('Est. cost')}</p>
            <p className="font-medium text-slate-700">${data.budget.estimatedCostUsd.toFixed(4)}</p>
          </div>
        </div>
      </section>

      {/* Connections Used */}
      <section>
        <SectionHeader title={t('Connections Used')} />
        {data.connectionsUsed.length === 0 ? (
          <EmptyState message={t('No external connections used.')} />
        ) : (
          <div className="space-y-1">
            {data.connectionsUsed.map((c, i) => (
              <div key={i} className="flex items-center justify-between py-1 border-b border-slate-100">
                <div>
                  <span className="text-xs font-medium text-slate-700">{c.connectionName}</span>
                  <span className="text-xs text-slate-400 ml-2">→ {c.toolName}</span>
                </div>
                <span className="text-xs text-slate-400">{formatTime(c.calledAt)}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
